// src/services/noiseDataService.ts
// Service untuk mengambil data statistik kebisingan dari peta (untuk chatbot)

import { NoiseSummary, NoiseSummaryDetail } from "../types/chatTypes";
import { NoiseCluster } from "../types/mapTypes";
import { noiseThresholds } from "../config/mapConfig";
import { getNoiseDescription } from "../utils/mapUtils";

/**
 * Menghitung summary statistik kebisingan dari data cluster yang aktif di peta.
 * Dipanggil oleh chatbot untuk memberikan informasi area ke user.
 */
export function computeNoiseSummaryFromClusters(clusters: NoiseCluster[]): NoiseSummary {
  let tenang = 0;
  let sedang = 0;
  let berisik = 0;
  let sangatBerisik = 0;
  const details: NoiseSummaryDetail[] = [];

  for (const cluster of clusters) {
    const avg = cluster.noiseLevelAvg ?? 0;
    if (avg <= 0) continue; // skip cluster tanpa data valid

    if (avg <= noiseThresholds.low) {
      tenang++;
    } else if (avg <= noiseThresholds.medium) {
      sedang++;
    } else if (avg <= noiseThresholds.high) {
      berisik++;
    } else {
      sangatBerisik++;
    }

    details.push({
      area: cluster.id,
      noiseLevel: avg,
      category: getNoiseDescription(avg),
      source: cluster.finalCategory || (cluster.noiseSources?.[0]) || undefined,
      coordinates: cluster.center,
    });
  }

  return {
    tenang,
    sedang,
    berisik,
    sangatBerisik,
    total: tenang + sedang + berisik + sangatBerisik,
    details,
  };
}

/**
 * Format NoiseSummary menjadi teks yang bisa disisipkan ke context RAG
 */
export function formatNoiseSummaryForContext(summary: NoiseSummary): string {
  if (summary.total === 0) {
    return "Saat ini belum ada data area kebisingan yang tersedia di peta.";
  }

  let text = `DATA STATISTIK WILAYAH KEBISINGAN SAAT INI (Total: ${summary.total} area):\n`;
  text += `🟢 Tenang (≤${noiseThresholds.low} dB): ${summary.tenang} area\n`;
  text += `🟡 Sedang (${noiseThresholds.low + 1}-${noiseThresholds.medium} dB): ${summary.sedang} area\n`;
  text += `🟠 Berisik (${noiseThresholds.medium + 1}-${noiseThresholds.high} dB): ${summary.berisik} area\n`;
  text += `🔴 Sangat Berisik (>${noiseThresholds.high} dB): ${summary.sangatBerisik} area\n`;

  // Tambah semua area dengan koordinat untuk navigasi
  if (summary.details && summary.details.length > 0) {
    const sorted = [...summary.details].sort((a, b) => b.noiseLevel - a.noiseLevel);
    text += `\nDaftar semua area (urut dari paling berisik):\n`;
    sorted.forEach((d, i) => {
      text += `${i + 1}. ${d.noiseLevel.toFixed(1)} dB (${d.category})`;
      if (d.coordinates) text += ` [Lokasi: ${d.coordinates[0].toFixed(5)},${d.coordinates[1].toFixed(5)}]`;
      if (d.source) text += ` [Sumber: ${d.source}]`;
      text += "\n";
    });
  }

  return text;
}

/**
 * Deteksi apakah user bertanya tentang statistik/jumlah area kebisingan
 */
export function isStatisticsQuery(query: string): boolean {
  const statsKeywords = [
    "jumlah", "berapa", "statistik", "total",
    "wilayah", "area", "zona", "region",
    "tenang", "berisik", "sangat berisik",
    "summary", "data", "banyak", "hitung",
    "report", "laporan", "cluster",
  ];
  const lower = query.toLowerCase();
  return statsKeywords.filter((kw) => lower.includes(kw)).length >= 2;
}

/**
 * Deteksi apakah user bertanya tentang lokasi wilayah tertentu (untuk flyTo)
 * Return: kategori yang diminta, atau null jika bukan location query
 */
export function detectLocationQuery(query: string): "tenang" | "sedang" | "berisik" | "sangatBerisik" | null {
  const lower = query.toLowerCase();
  const locationKeywords = ["dimana", "di mana", "mana", "lokasi", "tunjukkan", "tunjukan", "lihat", "arahkan", "pov", "zoom", "pergi ke", "navigate"];
  const hasLocationIntent = locationKeywords.some((kw) => lower.includes(kw));

  if (!hasLocationIntent) return null;

  if (lower.includes("sangat berisik") || lower.includes("sgt berisik") || lower.includes("paling berisik") || lower.includes("paling bising")) {
    return "sangatBerisik";
  }
  if (lower.includes("berisik") || lower.includes("bising") || lower.includes("ribut") || lower.includes("keras")) {
    return "berisik";
  }
  if (lower.includes("sedang") || lower.includes("menengah") || lower.includes("moderate")) {
    return "sedang";
  }
  if (lower.includes("tenang") || lower.includes("sunyi") || lower.includes("sepi") || lower.includes("hening")) {
    return "tenang";
  }

  return null;
}

/**
 * Cari cluster pertama yang cocok dengan kategori tertentu.
 * Return FlyToTarget atau null jika tidak ditemukan.
 */
export function findClusterForCategory(
  summary: NoiseSummary,
  category: "tenang" | "sedang" | "berisik" | "sangatBerisik"
): { coordinates: [number, number]; label: string; noiseLevel: number; category: string } | null {
  if (!summary.details || summary.details.length === 0) return null;

  const categoryMap: Record<string, string[]> = {
    tenang: ["Tenang"],
    sedang: ["Sedang"],
    berisik: ["Berisik"],
    sangatBerisik: ["Sangat Berisik"],
  };

  const matchLabels = categoryMap[category] || [];
  const match = summary.details.find((d) => matchLabels.some((l) => d.category.includes(l)));

  if (!match || !match.coordinates) return null;

  return {
    coordinates: match.coordinates,
    label: match.category,
    noiseLevel: match.noiseLevel,
    category: match.category,
  };
}
